// ─────────────────────────────────────────────────────────────
// CATALOGUE
//
// Quatre sources :
//   1. les bâtiments des CONFRÉRIES (sets.js) — injectés automatiquement
//   2. les habitants, récompenses de confrérie
//   3. le décor libre défini ci-dessous (nature, chemins, lampes, murs)
//   4. les objets créés par l'admin depuis l'interface (customItems.js)
//
// Champs d'un objet :
//   kind   "tile" (image du pack) ou "proc" (dessin vectoriel)
//   layer  "objet" (occupe la case) · "sol" (dessous) · "pnj" (se déplace)
//   size   [largeur, profondeur] en cases
//   parts  composition multi-tuiles, pour les emprises non carrées
//   price  force le prix (sinon celui de la rareté)
//   set / unlockedBy   rattachement à une confrérie
//
// IMPORTANT — mutabilité : CATALOG, BY_ID et CATEGORIES sont modifiés
// EN PLACE (jamais réassignés) quand l'admin ajoute un objet. Tous les
// modules qui les ont importés voient donc la mise à jour sans qu'on
// ait à recharger la page ni à recâbler les imports.
// ─────────────────────────────────────────────────────────────
import { drawLamp, drawVillager, drawAnimal } from "./sprites.js";
import { PAL } from "./tileset.js";
import { SETS } from "./sets.js";
import { t } from "@/i18n";

const tile = (
  id,
  nameKey,
  descKey,
  cat,
  rarity,
  tileKey,
  layer = "objet",
  price,
) => ({
  id,
  get name() {
    return t(nameKey);
  },
  get desc() {
    return t(descKey);
  },
  nameKey,
  descKey,
  cat,
  rarity,
  kind: "tile",
  tile: tileKey,
  layer,
  price,
  size: [1, 1],
});

// Une structure plus grande = plusieurs blocs voisins. On ne met
// JAMAIS un bloc à l'échelle : ça doublerait aussi la hauteur du cube.
const grid = (id, nameKey, descKey, cat, rarity, tileKey, w, d, price) => ({
  id,
  get name() {
    return t(nameKey);
  },
  get desc() {
    return t(descKey);
  },
  nameKey,
  descKey,
  cat,
  rarity,
  kind: "tile",
  tile: tileKey,
  layer: "objet",
  price,
  size: [w, d],
  parts: Array.from({ length: w * d }, (_, i) => ({
    tile: tileKey,
    dx: i % w,
    dy: Math.floor(i / w),
  })),
});

const combo = (id, nameKey, descKey, cat, rarity, size, parts, price) => ({
  id,
  get name() {
    return t(nameKey);
  },
  get desc() {
    return t(descKey);
  },
  nameKey,
  descKey,
  cat,
  rarity,
  kind: "tile",
  tile: parts[0].tile,
  layer: "objet",
  price,
  size,
  parts,
});

const proc = (
  id,
  nameKey,
  descKey,
  cat,
  rarity,
  draw,
  layer = "objet",
  price,
  extra = {},
) => ({
  id,
  get name() {
    return t(nameKey);
  },
  get desc() {
    return t(descKey);
  },
  nameKey,
  descKey,
  cat,
  rarity,
  kind: "proc",
  draw,
  layer,
  price,
  size: [1, 1],
  ...extra,
});

// ── Apparences des habitants ──────────────────────────────────
export const LOOKS = {
  marchand: {
    robe: "#f0e3cc",
    robeDark: "#d3c2a6",
    hat: PAL.terraDark,
    accent: "#c9a227",
  },
  guerrab: {
    robe: "#d9564a",
    robeDark: "#a83b32",
    hat: "#3f3a36",
    accent: "#e8c46a",
  },
  tisseuse: {
    robe: "#3fa5a5",
    robeDark: "#2b7878",
    hat: null,
    accent: "#f0e3cc",
  },
  conteur: {
    robe: "#8b5fa8",
    robeDark: "#63407a",
    hat: "#e8c46a",
    accent: "#f0e3cc",
  },
  musicien: {
    robe: "#4f7fc4",
    robeDark: "#375c96",
    hat: "#e8c46a",
    accent: "#ffffff",
  },
  enfant: {
    robe: "#f5b942",
    robeDark: "#cf9527",
    hat: null,
    accent: "#d9564a",
  },
  caravanier: {
    robe: "#7a6b52",
    robeDark: "#574b39",
    hat: "#c9a227",
    accent: "#d9564a",
  },
};
export const LOOK_IDS = Object.keys(LOOKS);

const villager = (look) => (ctx, x, y, s, t, facing, moving) =>
  drawVillager(ctx, x, y, s, LOOKS[look] || LOOKS.marchand, t, facing, moving);

// ── Animaux ───────────────────────────────────────────────────
export const BEASTS = {
  chat: {
    body: "#e3d3ae",
    dark: "#b39c72",
    ears: "pointy",
    height: 0.62,
    length: 0.66,
  },
  chevre: {
    body: "#f0e6d2",
    dark: "#bdae93",
    horn: "#8a7355",
    ears: "tall",
    height: 0.78,
    length: 0.8,
  },
  mouton: {
    body: "#f7f2e6",
    dark: "#c9bfa8",
    belly: "#ffffff",
    ears: "tall",
    height: 0.75,
    length: 0.85,
  },
  ane: { body: "#a89a86", dark: "#77695a", ears: "tall", height: 1, length: 1 },
  chameau: {
    body: "#d8b87c",
    dark: "#a3844f",
    hump: 1,
    ears: "tall",
    height: 1.25,
    length: 1.15,
  },
  faucon: {
    body: "#8d7a63",
    dark: "#5e4f3f",
    mane: "#e8ddc6",
    ears: "pointy",
    height: 0.5,
    length: 0.5,
  },
};
const beast = (look) => (ctx, x, y, s, t, facing, moving) =>
  drawAnimal(ctx, x, y, s, BEASTS[look], t, facing, moving);

const animal = (id, nameKey, descKey, rarity, look, price) => ({
  id,
  get name() {
    return t(nameKey);
  },
  get desc() {
    return t(descKey);
  },
  nameKey,
  descKey,
  cat: "animal",
  rarity,
  kind: "proc",
  draw: beast(look),
  layer: "animal",
  price,
  size: [1, 1],
});

// ── 1 & 2. Confréries d'origine : bâtiments + habitants ───────
const fromSets = [];
for (const set of SETS) {
  for (const bld of set.buildings) {
    fromSets.push({
      id: bld.id,
      name: bld.name,
      desc: bld.desc,
      cat: "batiment",
      rarity: bld.rarity,
      kind: "tile",
      tile: bld.tile,
      layer: "objet",
      size: bld.size || [1, 1],
      parts: bld.parts,
      set: set.id,
    });
  }
  fromSets.push({
    id: set.npc.id,
    name: set.npc.name,
    desc: set.npc.desc,
    cat: "pnj",
    rarity: "recompense",
    kind: "proc",
    draw: villager(set.npc.look),
    layer: "pnj",
    size: [1, 1],
    unlockedBy: set.id,
  });
}

// ── 3. Décor libre ────────────────────────────────────────────
const freeDecor = [
  // Chemins et lumière — volontairement très bon marché
  tile(
    "chemin_terre",
    "catalog.chemin_terre_name",
    "catalog.chemin_terre_desc",
    "sol",
    "commun",
    "dirtLow",
    "sol",
    15,
  ),
  tile(
    "chemin_dalles",
    "catalog.chemin_dalles_name",
    "catalog.chemin_dalles_desc",
    "sol",
    "commun",
    "floorTiles",
    "sol",
    20,
  ),
  tile(
    "chemin_ruines",
    "catalog.chemin_ruines_name",
    "catalog.chemin_ruines_desc",
    "sol",
    "commun",
    "floorCrumbled",
    "sol",
    20,
  ),
  tile(
    "chemin_orne",
    "catalog.chemin_orne_name",
    "catalog.chemin_orne_desc",
    "sol",
    "commun",
    "floorDecorated",
    "sol",
    35,
  ),
  proc(
    "lampe",
    "catalog.lampe_name",
    "catalog.lampe_desc",
    "objet",
    "commun",
    (ctx, x, y, s, t) => drawLamp(ctx, x, y, s, t),
    "objet",
    25,
    { emitsLight: true },
  ),

  // Animaux
  animal(
    "chat",
    "catalog.chat_name",
    "catalog.chat_desc",
    "commun",
    "chat",
    90,
  ),
  animal(
    "chevre",
    "catalog.chevre_name",
    "catalog.chevre_desc",
    "commun",
    "chevre",
    140,
  ),
  animal(
    "mouton",
    "catalog.mouton_name",
    "catalog.mouton_desc",
    "peu_commun",
    "mouton",
  ),
  animal("ane", "catalog.ane_name", "catalog.ane_desc", "peu_commun", "ane"),
  animal(
    "chameau",
    "catalog.chameau_name",
    "catalog.chameau_desc",
    "rare",
    "chameau",
  ),
  animal(
    "faucon",
    "catalog.faucon_name",
    "catalog.faucon_desc",
    "rare",
    "faucon",
  ),

  // Nature
  tile(
    "rocher",
    "catalog.rocher_name",
    "catalog.rocher_desc",
    "nature",
    "commun",
    "rocks",
  ),
  tile(
    "palmier",
    "catalog.palmier_name",
    "catalog.palmier_desc",
    "nature",
    "commun",
    "tree",
  ),
  tile(
    "bosquet",
    "catalog.bosquet_name",
    "catalog.bosquet_desc",
    "nature",
    "commun",
    "trees",
  ),
  grid(
    "palmeraie",
    "catalog.palmeraie_name",
    "catalog.palmeraie_desc",
    "nature",
    "rare",
    "tree",
    2,
    2,
  ),

  // Structures isolées
  tile(
    "escalier",
    "catalog.escalier_name",
    "catalog.escalier_desc",
    "objet",
    "commun",
    "stairsFull",
  ),
  tile(
    "escalier_droit",
    "catalog.escalier_droit_name",
    "catalog.escalier_droit_desc",
    "objet",
    "peu_commun",
    "stairsRight",
  ),
  tile(
    "mur_abime",
    "catalog.mur_abime_name",
    "catalog.mur_abime_desc",
    "batiment",
    "commun",
    "wallBroken",
  ),
  tile(
    "mur_enceinte",
    "catalog.mur_enceinte_name",
    "catalog.mur_enceinte_desc",
    "batiment",
    "peu_commun",
    "wallSquare",
  ),
  tile(
    "angle_mur",
    "catalog.angle_mur_name",
    "catalog.angle_mur_desc",
    "batiment",
    "peu_commun",
    "wallCorner",
  ),
  tile(
    "mur_facade",
    "catalog.mur_facade_name",
    "catalog.mur_facade_desc",
    "batiment",
    "rare",
    "wallLeft",
  ),
  tile(
    "petit_dome",
    "catalog.petit_dome_name",
    "catalog.petit_dome_desc",
    "batiment",
    "peu_commun",
    "domeSmall",
  ),
  grid(
    "quartier_domes",
    "catalog.quartier_domes_name",
    "catalog.quartier_domes_desc",
    "batiment",
    "epique",
    "dome",
    2,
    2,
  ),
  combo(
    "rempart",
    "catalog.rempart_name",
    "catalog.rempart_desc",
    "batiment",
    "peu_commun",
    [3, 1],
    [
      { tile: "wallCorner", dx: 0, dy: 0 },
      { tile: "wallSides", dx: 1, dy: 0 },
      { tile: "wallEnd", dx: 2, dy: 0 },
    ],
    420,
  ),
];

// Tableaux MUTÉS en place — voir la note en tête de fichier.
export const CATALOG = [...fromSets, ...freeDecor];
export const BY_ID = Object.fromEntries(CATALOG.map((i) => [i.id, i]));

const BASE_IDS = new Set(CATALOG.map((i) => i.id));

export const isNpc = (item) => item.layer === "pnj";
export const isShopItem = (item) =>
  !isNpc(item) && item.rarity !== "epique" && item.rarity !== "legendaire";

export const BASE_CATEGORIES = [
  {
    id: "all",
    labelKey: "catalog.cat_all",
    get label() {
      return t(this.labelKey);
    },
  },
  {
    id: "animal",
    labelKey: "catalog.cat_animal",
    get label() {
      return t(this.labelKey);
    },
  },
  {
    id: "sol",
    labelKey: "catalog.cat_sol",
    get label() {
      return t(this.labelKey);
    },
  },
  {
    id: "batiment",
    labelKey: "catalog.cat_batiment",
    get label() {
      return t(this.labelKey);
    },
  },
  {
    id: "nature",
    labelKey: "catalog.cat_nature",
    get label() {
      return t(this.labelKey);
    },
  },
  {
    id: "objet",
    labelKey: "catalog.cat_objet",
    get label() {
      return t(this.labelKey);
    },
  },
  {
    id: "pnj",
    labelKey: "catalog.cat_pnj",
    get label() {
      return t(this.labelKey);
    },
  },
];
export const CATEGORIES = [...BASE_CATEGORIES];

export const categoryLabel = (id) =>
  CATEGORIES.find((c) => c.id === id)?.label || id || t("catalog.cat_divers");

export function poolFor(rarity) {
  return CATALOG.filter((i) => i.rarity === rarity && !isNpc(i));
}

function dropCustom() {
  for (let i = CATALOG.length - 1; i >= 0; i--) {
    if (!BASE_IDS.has(CATALOG[i].id)) {
      delete BY_ID[CATALOG[i].id];
      CATALOG.splice(i, 1);
    }
  }
}

export function setCustomCategories(defs = []) {
  CATEGORIES.length = 0;
  CATEGORIES.push(...BASE_CATEGORIES);
  for (const c of defs) {
    if (!c?.id || CATEGORIES.some((x) => x.id === c.id)) continue;
    CATEGORIES.push({ id: c.id, label: c.label || c.id, custom: true });
  }
}

export function setCustomItems(items = [], confreries = []) {
  dropCustom();

  for (const d of items) {
    if (!d?.id || BASE_IDS.has(d.id)) continue;
    const w = Math.max(1, d.size?.[0] || 1);
    const h = Math.max(1, d.size?.[1] || 1);
    const entry = {
      id: d.id,
      name: d.name || d.id,
      desc: d.desc || "",
      cat: d.cat || "objet",
      rarity: d.rarity || "commun",
      kind: "tile",
      tile: d.tile || null,
      layer: d.layer === "sol" ? "sol" : "objet",
      price: typeof d.price === "number" ? d.price : undefined,
      resale: typeof d.resale === "number" ? d.resale : undefined,
      size: [w, h],
      set: d.set || undefined,
      custom: true,
    };
    CATALOG.push(entry);
    BY_ID[entry.id] = entry;
  }

  for (const s of confreries) {
    if (!s?.npc?.id || BY_ID[s.npc.id]) continue;
    const entry = {
      id: s.npc.id,
      name: s.npc.name || s.npc.id,
      desc: s.npc.desc || "",
      cat: "pnj",
      rarity: "recompense",
      kind: "proc",
      draw: villager(s.npc.look),
      layer: "pnj",
      size: [1, 1],
      unlockedBy: s.id,
      custom: true,
    };
    CATALOG.push(entry);
    BY_ID[entry.id] = entry;
  }
}
